import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gruposItensApi, listasComprasApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader } from '../../components/ui/Card'
import { ErrorState, LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { ConfirmDialog, Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { cn, formatMoney } from '../../lib/utils'
import { ConciliarModal } from './ConciliarModal'
import type { AdicionarItemListaRequest, ItemLista } from '../../types'

type Unidade = 'KG' | 'L' | 'UN'

function pesoTexto(p: { valor: number; unidade: Unidade } | null): string {
  return p ? `${p.valor} ${p.unidade === 'UN' ? 'un' : p.unidade === 'L' ? 'L' : 'kg'}` : ''
}

/** Uma lista aberta: renomear, itens por grupo (marca e peso opcionais), marcar, conciliar com nota e excluir. */
export function ListaDetalhe({ listaId, aoRemover }: { listaId: string; aoRemover: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [item, setItem] = useState<ItemLista | 'novo' | null>(null)
  const [renomeando, setRenomeando] = useState<string | null>(null)
  const [excluindo, setExcluindo] = useState(false)
  const [conciliando, setConciliando] = useState(false)

  const { data: lista, isLoading, isError, error } = useQuery({ queryKey: ['lista-compras', listaId], queryFn: () => listasComprasApi.obter(listaId) })

  const refaz = () => { qc.invalidateQueries({ queryKey: ['lista-compras', listaId] }); qc.invalidateQueries({ queryKey: ['listas-compras'] }) }
  const onError = (e: Error) => toast.error(e.message)

  const renomear = useMutation({ mutationFn: (nome: string) => listasComprasApi.renomear(listaId, nome), onSuccess: () => { refaz(); setRenomeando(null) }, onError })
  const remover = useMutation({ mutationFn: () => listasComprasApi.remover(listaId), onSuccess: () => { qc.invalidateQueries({ queryKey: ['listas-compras'] }); toast.success('Lista excluída.'); aoRemover() }, onError })
  const marcar = useMutation({ mutationFn: (v: { itemId: string; marcado: boolean; quantidadeComprada?: number }) => listasComprasApi.marcarItem(listaId, v.itemId, v.marcado, v.quantidadeComprada), onSuccess: refaz, onError })
  const removerItem = useMutation({ mutationFn: (itemId: string) => listasComprasApi.removerItem(listaId, itemId), onSuccess: refaz, onError })

  if (isLoading) return <Card><LoadingState /></Card>
  if (isError || !lista) return <Card><ErrorState message={(error as Error | null)?.message ?? 'Não consegui abrir a lista.'} /></Card>

  const pendentes = lista.itens.filter((i) => i.status === 1)
  const comprados = lista.itens.filter((i) => i.status === 2)

  return (
    <>
      <Card>
        <CardHeader
          title={
            renomeando !== null ? (
              <span className="flex items-center gap-2">
                <input className="form-control h-8" value={renomeando} autoFocus aria-label="Nome da lista" onChange={(e) => setRenomeando(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && renomeando.trim()) renomear.mutate(renomeando.trim()); if (e.key === 'Escape') setRenomeando(null) }} />
                <Button size="sm" disabled={!renomeando.trim()} loading={renomear.isPending} onClick={() => renomear.mutate(renomeando.trim())}>Salvar</Button>
              </span>
            ) : lista.nome
          }
          icon="ri-list-check-2"
          actions={<>
            {renomeando === null && <IconButton icon="ri-pencil-line" label="Renomear lista" onClick={() => setRenomeando(lista.nome)} />}
            <Button data-tutorial="lista.escanear" variant="soft-info" icon="ri-receipt-line" onClick={() => setConciliando(true)}>Conciliar com nota</Button>
            <Button data-tutorial="lista.item" icon="ri-add-line" onClick={() => setItem('novo')}>Adicionar item</Button>
            <IconButton icon="ri-delete-bin-5-fill" label="Excluir lista" tone="danger" onClick={() => setExcluindo(true)} />
          </>}
        />
        <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-4 py-2 text-13 text-muted">
          {lista.compartilhada && <Badge variant="info">Compartilhada · de {lista.donoNome}</Badge>}
          <span>{comprados.length} de {lista.itens.filter((i) => i.status !== 3).length} comprados</span>
          <span>· estimado {formatMoney(lista.estimado)}</span>
        </div>

        {lista.itens.length === 0 ? (
          <EmptyState icon="ri-shopping-basket-line" message="Lista vazia. Adicione um grupo (ex.: Leite em pó) com marca ou peso se quiser." action={<Button data-tutorial="lista.item" variant="soft-primary" icon="ri-add-line" onClick={() => setItem('novo')}>Adicionar item</Button>} />
        ) : (
          <ul className="divide-y divide-border/50">
            {[...pendentes, ...comprados].map((i) => {
              const feito = i.status === 2
              return (
                <li key={i.id} className="flex items-start gap-3 px-4 py-3">
                  <input
                    data-tutorial="lista.baixa" type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={feito} disabled={marcar.isPending}
                    aria-label={`Marcar ${i.nome}`} onChange={(e) => marcar.mutate({ itemId: i.id, marcado: e.target.checked })}
                  />
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-white', feito && 'text-muted line-through')}>
                      {i.nome} <span className="text-muted">· {i.quantidade > 1 ? `${i.quantidadeComprada} de ${i.quantidade}` : i.quantidade}</span>
                      {i.marcaFixa && <Badge variant="primary" className="ml-2">{i.marcaFixa}</Badge>}
                      {i.pesoFixo && <Badge variant="muted" className="ml-1">{pesoTexto(i.pesoFixo)}</Badge>}
                    </p>
                    {i.dica && <p className="text-2xs text-muted">Última compra: {[i.dica.marca, i.dica.pesoBase, formatMoney(i.dica.valorUnitario), i.dica.estabelecimento].filter(Boolean).join(' · ')}</p>}
                    {i.quantidade > 1 && i.status !== 3 && (
                      <QuantidadeComprada
                        key={`${i.id}-${i.quantidadeComprada}`} item={i} disabled={marcar.isPending}
                        onConfirmar={(q) => marcar.mutate(q <= 0 ? { itemId: i.id, marcado: false } : { itemId: i.id, marcado: true, quantidadeComprada: q })}
                      />
                    )}
                    {i.quantidadeComprada > 0 && i.marcadoPorNome && <p className="text-2xs text-income">marcado por {i.marcadoPorNome}</p>}
                  </div>
                  <IconButton icon="ri-pencil-fill" label={`Editar ${i.nome}`} tone="info" onClick={() => setItem(i)} />
                  <IconButton icon="ri-close-line" label={`Remover ${i.nome}`} tone="danger" onClick={() => removerItem.mutate(i.id)} />
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      {item && <ItemListaModal listaId={listaId} item={item === 'novo' ? null : item} onClose={() => setItem(null)} onSalvo={refaz} />}
      {conciliando && <ConciliarModal listaId={listaId} onClose={() => setConciliando(false)} onAplicado={refaz} />}
      <ConfirmDialog
        open={excluindo} onClose={() => setExcluindo(false)} loading={remover.isPending} onConfirm={() => remover.mutate()}
        message={<>A lista <strong className="text-white">{lista.nome}</strong> e seus itens serão excluídos.{lista.compartilhada && ' Quem tem acesso deixa de ver a lista.'}</>}
      />
    </>
  )
}

/** Baixa parcial: informa quanto já comprou (0 desmarca; a quantidade toda conclui o item). */
function QuantidadeComprada({ item, disabled, onConfirmar }: { item: ItemLista; disabled: boolean; onConfirmar: (q: number) => void }) {
  const [valor, setValor] = useState(String(item.quantidadeComprada))
  const q = Number(valor.replace(',', '.'))
  const valido = valor.trim() !== '' && q >= 0 && q <= item.quantidade
  const mudou = valido && q !== item.quantidadeComprada
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-2xs text-muted">
      <span>Comprei</span>
      <input
        type="number" min={0} max={item.quantidade} step="any" className="form-control h-8 w-20" value={valor} aria-label={`Quantidade comprada de ${item.nome}`}
        onChange={(e) => setValor(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && mudou) onConfirmar(q) }}
      />
      <span>de {item.quantidade}</span>
      <Button size="sm" variant="light" disabled={disabled || !mudou} onClick={() => onConfirmar(q)}>Confirmar</Button>
      {item.status !== 2 && <Button size="sm" variant="light" disabled={disabled} onClick={() => onConfirmar(item.quantidade)}>Todos</Button>}
    </div>
  )
}

/** Adiciona ou edita um item: grupo (ou texto livre), quantidade, marca fixa e peso fixo opcionais. */
function ItemListaModal({ listaId, item, onClose, onSalvo }: { listaId: string; item: ItemLista | null; onClose: () => void; onSalvo: () => void }) {
  const toast = useToast()
  const { data: grupos = [] } = useQuery({ queryKey: ['itens-grupos'], queryFn: gruposItensApi.list })
  const { data: marcas = [] } = useQuery({ queryKey: ['itens-marcas'], queryFn: gruposItensApi.marcas, staleTime: 5 * 60_000 })
  const [grupoId, setGrupoId] = useState(item?.grupoId ?? '')
  const [livre, setLivre] = useState(item && !item.grupoId ? item.nome : '')
  const [quantidade, setQuantidade] = useState(String(item?.quantidade ?? 1))
  const [marca, setMarca] = useState(item?.marcaFixa ?? '')
  const [pesoValor, setPesoValor] = useState(item?.pesoFixo ? String(item.pesoFixo.valor) : '')
  const [pesoUnidade, setPesoUnidade] = useState<Unidade>(item?.pesoFixo?.unidade ?? 'KG')
  const [erro, setErro] = useState<string | null>(null)

  const qtd = Number(quantidade.replace(',', '.'))
  const pode = (!!grupoId || livre.trim().length > 0) && qtd > 0

  const salvar = useMutation({
    mutationFn: () => {
      const peso = Number(pesoValor.replace(',', '.'))
      const dados: AdicionarItemListaRequest = {
        grupoId: grupoId || null, descricaoLivre: grupoId ? null : livre.trim(), quantidade: qtd,
        marcaFixa: marca.trim() || null, pesoFixoValor: peso > 0 ? peso : null, pesoFixoUnidade: peso > 0 ? pesoUnidade : null,
      }
      return item ? listasComprasApi.atualizarItem(listaId, item.id, dados) : listasComprasApi.adicionarItem(listaId, dados)
    },
    onSuccess: () => { onSalvo(); toast.success(item ? 'Item atualizado.' : 'Item adicionado.'); onClose() },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title={item ? 'Editar item' : 'Adicionar item'}
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!pode} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button></>}
    >
      <div className="space-y-4">
        <Select label="Grupo" value={grupoId} onChange={(e) => setGrupoId(e.target.value)}>
          <option value="">Sem grupo (texto livre)</option>
          {grupos.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
        </Select>
        {!grupoId && <Input label="Item" value={livre} onChange={(e) => setLivre(e.target.value)} placeholder="Ex.: Pão de queijo" autoFocus />}
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Quantidade" type="number" min={0} step="0.001" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
          <div>
            <datalist id="marcas-lista">{marcas.map((m) => <option key={m} value={m} />)}</datalist>
            <Input label="Marca fixa (opcional)" list="marcas-lista" value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Ex.: Ninho" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Peso fixo (opcional)" type="number" min={0} step="0.001" value={pesoValor} onChange={(e) => setPesoValor(e.target.value)} placeholder="Ex.: 0,9" />
          <Select label="Unidade" value={pesoUnidade} onChange={(e) => setPesoUnidade(e.target.value as Unidade)}>
            <option value="KG">Quilo (kg)</option><option value="L">Litro (L)</option><option value="UN">Unidade</option>
          </Select>
        </div>
        <p className="text-2xs text-muted">Com marca ou peso fixos, só uma compra desse tipo dá baixa no item ao conciliar a nota.</p>
        {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
      </div>
    </Modal>
  )
}
